import { useNavigate, useParams } from 'react-router-dom';
import { IGame, IGameSocial } from '../../../interfaces';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Footer from '../../footer';
import Loader from '../../loader';
import { getGameData } from '../../../stores/games.stores';
import { toast } from 'react-toastify';
import HeaderGames from '../header';
import { PlayNowSvg } from '../../../icons';
import { useTranslation } from 'react-i18next';
import { GAME_IFRAME_ID, STATIC_FILES_PATH } from '../../../common/constants';
import i18n from 'i18next';
import { setDocumentData } from '../../../common/tools';

const FOOTER_HIDDEN_ELEMS: string[] = [ 'llc', 'contacts', 'offer', 'personalData' ];
const FOOTER_CLASS_NAME: string = 'game-footer';


export default function Game(): JSX.Element {
    const { projectName } = useParams<{ projectName: string }>();
    const [ gameData, setGameData ] = useState<IGame>();
    const [ social, setSocial ] = useState<IGameSocial[]>();
    const [ hideIframe, setHideIframe ] = useState<boolean>(true);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { language } = i18n;

    useEffect(() => {
        if (projectName) {
            _getGameData();
        }
    }, [ projectName ]);

    useEffect(() => {
        i18n.on('languageChanged', (lng: string) => {
            _getGameData();
        });

        return () => {
            i18n.off('languageChanged');
        };
    }, []);

    const _getGameData = () => {
        getGameData(projectName as string).then(res => {
            const gameData: IGame = res?.data;

            if (!gameData || !gameData?.web_page || !gameData?.web_page?.isActive || gameData?.web_page?.inDevelop) {
                navigate('/games');
                return;
            }

            setGameData(gameData);
            setSocial(gameData.social?.items || null);
        }).catch(err => {
            console.error('Error loading game data:', err?.response?.data || err);
            toast.error('GET_GAME_DATA_ERROR');
        });
    }

    const iframeSrc = useMemo(() => gameData?.web_page?.iframeUrl || '', [ gameData ]);

    const backgroundImage = useMemo(() =>
            gameData?.web_page?.projectBackgroundFile
                ? `url(${STATIC_FILES_PATH}${gameData.web_page.projectBackgroundFile})`
                : '',
        [ gameData ]
    );

    const gameImage = useMemo(() =>
            gameData?.web_page?.gameImage
                ? `${STATIC_FILES_PATH}${gameData.web_page.gameImage}`
                : '',
        [ gameData ]
    );

    useEffect(() => {
        if (gameData?.seoData) {
            setDocumentData(gameData?.seoData);
        }
    }, [ gameData ]);

    const _renderSocial = useCallback(() => {
        if (!social) {
            return null;
        }

        return (
            <div className="game-social">
                {
                    social.map((elem: IGameSocial) => {
                        const srcPath = `${STATIC_FILES_PATH}${elem.fileName}`;

                        return (
                            <img
                                key={elem.fileName}
                                className="game-social-item"
                                alt=""
                                src={srcPath}
                                onClick={() => window.open(elem.link, '_blank')}
                            />
                        )
                    })
                }
            </div>
        );
    }, [ social ]);

    const _renderGamePage = () => {
        return (
            <div className="game-page">
                <div className="game-page-image-box">
                    <img className="game-page-image" alt="" src={gameImage}/>
                </div>
                <div className="game-page-menu-box">
                    <div className="game-page-menu-box-menu">
                        <div className="game-page-menu-box-menu-title">
                            { gameData?.localization?.name || 'Game'}
                        </div>
                        <div className="game-page-menu-box-menu-description">
                            { gameData?.localization?.fullDescription || '' }
                        </div>
                    </div>
                    <div className="game-page-menu-box-btn" onClick={() => setHideIframe(false)}>
                        <div className="game-page-menu-box-btn-icon">
                            <PlayNowSvg/>
                        </div>
                        <div className="game-page-menu-box-btn-text">
                            { t('games.buttonPlay') }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const _renderIframe = () => {
        const iframeUrl = new URL(iframeSrc);
        iframeUrl.searchParams.set('language', language);

        return (
            <div className="game-page">
                <iframe src={iframeUrl.toString()} id={GAME_IFRAME_ID} className="game-box-frame"/>
            </div>
        );
    }

    if (!gameData) {
        return <Loader/>;
    }

    return (
        <div className="game">
            {
                !hideIframe ?
                    <div className="game-background" style={{background: backgroundImage}}/>
                : null
            }
            <HeaderGames isGame={true} project={gameData?.project}/>
            { hideIframe ? _renderGamePage() : _renderIframe() }
            <Footer hiddenElems={FOOTER_HIDDEN_ELEMS} className={FOOTER_CLASS_NAME}/>
            { _renderSocial() }
        </div>
    );
}